import { Component, inject, OnInit, ViewEncapsulation, PLATFORM_ID } from '@angular/core';
import { OLAPrams } from '@workflow/ecp-workflow/ola/types/constants';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { getFlowInfo, FlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { ConsumerLoanGroup } from '@shared/model/lender-config.model';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { isPlatformBrowser } from '@angular/common';

@BuildingBlock('ola-loan-type')
@Component({
  selector: 'ecp-loan-type',
  styleUrls: ['./ola-loan-type.component.scss'],
  templateUrl: './ola-loan-type.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class OLALoanTypeComponent extends BuildingBlockControlBaseComponent implements OnInit {
  /** loanGroup */
  public loanGroup: string;
  /** loanType */
  public loanType: string;
  /** UI Template */
  public uiTemplate: string;
  /** consumer loan group */
  public consumerLoanGroups: ConsumerLoanGroup[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectedConsumerLoanGroup: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public loanProductDescription: any;
  public loanProductDescriptionEnabled = false;
  public isPrefillByParams = false;
  // Check List Description Storage Path
  public checklistDescriptionPath = 'ss:ola-loan-product-checklist-description';
  // Check List Enabled Storage Path
  public isEnabledChecklistPath = 'ss:ola-loan-product-checklist-enabled';
  public enableEnhancedProductSelection: boolean;
  public allProductsVisible = false;
  public maxInitialProducts = 8;

  public flowService = inject(FlowService);
  public workflowPageNavigationService = inject(WorkflowPageNavigationStateService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  public ngOnInit(): void {
    if (this.isBrowser) {
      window.scrollTo(0, 0);
    }
    this.enableEnhancedProductSelection = this.lenderService.lenderData.enableEnhancedProductSelection;
    this.consumerLoanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups
      ? this.adminPortalFeaturesService?.config?.olaConfiguration.consumerLoanGroups.filter((t) => t.isActive && !t.hideInUI)
      : null;

    if (this.consumerLoanGroups) {
      this.userService.flowType = 'OLA';
      this.workflowPageNavigationService.refreshPageState();
      this.setPrefillByParam(false);
      this.olaPrefill();
      // Callback method passed & this will be called once all params are set inside loadvalues method.
      this.loadValues(() => {
        this.skipPrefilledWelcomePage();
      });
    }
    this.subscribemodelchanges();
  }

  /**
   * If loan groupname available then skip the loan selection page & redirect to next page.
   */
  public skipPrefilledWelcomePage(): void {
    if (this.activeRoute.snapshot.queryParams[OLAPrams.LoanType]) {
      if (this.loanGroup) {
        // navigating to next page using workflownavigationservice
        this.statefulService.wrap('ss:ola-fresh-loan').value = true;
        this.redirectToNextPage();
      }
    }
  }

  /**
   * Method to redirect To Next Page based on the configuration
   */
  private redirectToNextPage(): void {
    this.statefulService.wrap('ss:is-app-has-no-template').value = false;
    const uiTemplate = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    let templates = [];
    if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
      templates = this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates;
    }
    const template = templates.filter((t) => t.templateName.toLowerCase() === uiTemplate.toLowerCase())[0];
    const flow = this.flowService.createFlow(template.path);
    const flowInfo = getFlowInfo(flow);
    const path = this.getOLAFirstVisiblePage(flow, flowInfo, template.path);
    this.router.navigate(['app/' + path]);
  }

  /**
   * method to set ola first page
   * @param flow
   * @param flowInfo
   * @param workflowPrefix
   * @returns
   */
  private getOLAFirstVisiblePage(flow: FlowInstance, flowInfo: FlowInfo, workflowPrefix: string): string {
    let firstVisiblePage = 'ola/ola-consumer-loan-type';
    if (flow.id.toLowerCase() === workflowPrefix) {
      const visiblePages = flow.pages.filter((t) => t.visible && t.id !== 'ola-consumer-loan-type');
      if (visiblePages && visiblePages.length > 0) {
        firstVisiblePage = `${flowInfo.configPath}/${visiblePages[0].path}`;
      }
    }
    return firstVisiblePage;
  }

  private subscribemodelchanges(): void {
    this.statefulService.wrap('ss:ola-loan-group-confirm-popup-close').onChange(() => {
      this.loadValues();
    });
  }

  public clearLoanGroupandLoanType(clearAll: boolean = true) {
    if (this.isBrowser) {
      window.scrollTo(0, 0);
    }
    if (clearAll) {
      this.loanGroup = null;
      this.statefulService.wrap('ss:ola-new-loan-group').value = '';
    }
    this.loanType = null;
    this.statefulService.wrap('ss:ola-new-loan-type').value = '';
  }

  private loadValues(callBack?: () => void): void {
    this.loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    this.loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    this.uiTemplate = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    this.statefulService.wrap('ss:ola-new-loan-group').value = this.loanGroup ? this.loanGroup : '';
    this.statefulService.wrap('ss:ola-new-loan-type').value = this.loanType ? this.loanType : '';
    this.statefulService.wrap('ss:ola-new-ui-template').value = this.uiTemplate ? this.uiTemplate : '';

    if (this.loanGroup && this.consumerLoanGroups && this.consumerLoanGroups.length > 0) {
      this.setLoanGroupAndProducts(this.loanGroup);
      if (this.selectedConsumerLoanGroup) {
        const loanProduct = this.selectedConsumerLoanGroup.loanProducts.filter(
          (t) => !this.selectedConsumerLoanGroup?.isLoanGroupHasProduct || t.productName === this.loanType
        );
        if (loanProduct && loanProduct.length > 0) {
          this.onChangeLoantype(loanProduct[0]);
          if (callBack) {
            callBack();
          }
        } else {
          this.setPrefillByParam(false);
          this.clearLoanGroupandLoanType(false);
        }
      } else {
        this.setPrefillByParam(false);
        this.clearLoanGroupandLoanType();
      }
    }
  }

  public onChangeLoanGroup(loanGroup): void {
    this.setLoanGroupAndProducts(loanGroup);
    if (!this.selectedConsumerLoanGroup?.isLoanGroupHasProduct) {
      const selectedLoanType = this.selectedConsumerLoanGroup.loanProducts.filter(
        (t) => !this.selectedConsumerLoanGroup?.isLoanGroupHasProduct || t.productName === this.loanGroup
      )[0];
      this.loanProductDescription = selectedLoanType && selectedLoanType.description;
      this.loanProductDescriptionEnabled =
        selectedLoanType && (selectedLoanType.isDescriptionEnabled || (!selectedLoanType.version && selectedLoanType.description));
      this.statefulService.wrap(this.checklistDescriptionPath).value = selectedLoanType?.checklistDescription;
      this.statefulService.wrap(this.isEnabledChecklistPath).value = selectedLoanType?.isEnabledChecklist;
    } else {
      // Just added this check in case there're a lot of Loan Group options to choose from and
      // when a User chooses a Loan Group towards the bottom of the screen with only one(1) Loan product, the page doesn't scroll
      if (this.selectedConsumerLoanGroup?.isLoanGroupHasProduct && this.selectedConsumerLoanGroup.loanProducts.length > 1) {
        if (this.isBrowser) {
          window.scrollTo(0, 0);
        }
      }
      this.loanProductDescription = '';
      this.statefulService.wrap(this.checklistDescriptionPath).value = '';
      this.statefulService.wrap(this.isEnabledChecklistPath).value = false;
    }
    this.dynamicPageService.pageSubmitted = false;
    this.statefulService.wrap('ss:ola-new-loan-group').value = this.loanGroup;
    this.statefulService.wrap('ss:ola-new-ui-template').value = this.uiTemplate;
    this.statefulService.wrap('ss:ola-new-loan-type').value = this.loanType = '';
    if (this.isPrefillByParams && this.selectedConsumerLoanGroup) {
      this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value = this.loanGroup;
    }

    // Handling the scenario of a Loan Group having a single(1) Product
    // If having only one active loan product under loan group then
    // by default set the loan product
    if (this.selectedConsumerLoanGroup.loanProducts.length === 1) {
      this.loanType = this.selectedConsumerLoanGroup.loanProducts[0].productName;
      this.onChangeLoantype(this.selectedConsumerLoanGroup.loanProducts[0]);
    }
  }

  /**
   * Set Loan Group & Its Active Loan Products
   */
  public setLoanGroupAndProducts(loanGroup): void {
    if (this.enableEnhancedProductSelection) {
      this.loanGroup = loanGroup;
    }
    if (this.consumerLoanGroups) {
      const loanGroups = this.consumerLoanGroups.filter((t) => t.loanGroup === this.loanGroup);
      if (loanGroups?.length > 0) {
        this.selectedConsumerLoanGroup = loanGroups[0];
        this.selectedConsumerLoanGroup.loanProducts = this.selectedConsumerLoanGroup.loanProducts
          ? this.selectedConsumerLoanGroup.loanProducts.filter((t) => t.isActive === true)
          : [];
        if (this.selectedConsumerLoanGroup.loanProducts.length === 1) {
          this.statefulService.wrap('ss:hide-ola-requested-loan-amount').value =
            !this.selectedConsumerLoanGroup.loanProducts[0].ignoreLoanAmountSettings;
        }
      }
    }
  }

  public olaPrefill = (): void => {
    let loantype = this.checkloanTypeCaseSensitive(this.getFromSession('loantype'));
    let loantypeoption = this.checkloanTypeOptionCaseSensitive(loantype, this.getFromSession('loantypeoption'));
    if (this.activeRoute.snapshot.queryParams) {
      const params = this.activeRoute.snapshot.queryParams;
      loantype = this.checkloanTypeCaseSensitive(params[OLAPrams.LoanType]);
      loantypeoption = params[OLAPrams.LoanTypeOption];
      const page = this.workflowService.findFirstIncompletePage();
      const flow = this.flowService.createFlow(this.page.flowId);
      const currentPageId = flow.pages.findIndex((t) => t?.id === this.page?.id);
      const incompletePageId = flow.pages.findIndex((t) => t?.id === page?.id);
      // This to preserve redirecting to actual flow when splash screen enabled on deep link.
      if (incompletePageId < currentPageId) {
        return;
      }
    }
    // If loantype is available then consider it is deep linked
    if (loantype) {
      this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value = loantype;
      this.loanGroup = loantype;
      if (this.isBrowser) {
        sessionStorage.removeItem('loantype');
      }
      if (loantypeoption) {
        this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value = loantypeoption;
        this.loanType = loantypeoption;
        if (this.isBrowser) {
          sessionStorage.removeItem('loantypeoption');
        }
      }
      this.setPrefillByParam(true);
      this.isPrefillByParams = true;
      this.dynamicPageService.updateNavState(this.page, true);
      this.pageNavigationStateService.update(this.page.id, { state: 'completed' });
    }
  };

  /**
   * Set mem variable by checking Is Prefilled By Param
   * Also hide page progress bar if Prefilled by Param
   * @param isPrefilled boolean
   */
  private setPrefillByParam(isPrefilled: boolean): void {
    this.statefulService.wrap('mem:product-selection-prefilled-by-param').value = isPrefilled;
  }

  /**
   * Check case sensitive when Loantype provided through URL Parameter
   */
  private checkloanTypeCaseSensitive(loantype: string): string {
    const selectedLoanGroups = this.consumerLoanGroups.filter(
      (t) => loantype && t.displayName.toLocaleLowerCase().trim() === loantype.toLocaleLowerCase().trim()
    )[0];
    return selectedLoanGroups ? selectedLoanGroups.loanGroup : loantype;
  }

  /**
   * Check case sensitive when Loantype provided through URL Parameter
   */
  private checkloanTypeOptionCaseSensitive(loantype: string, loantypeoption: string): string {
    const selectedLoanGroup = this.consumerLoanGroups.filter(
      (t) => loantype && t.loanGroup.toLocaleLowerCase().trim() === loantype.toLocaleLowerCase().trim()
    )[0];
    const loanProduct =
      selectedLoanGroup?.loanProducts.filter(
        (t) => loantype && loantypeoption && t.productName.toLocaleLowerCase().trim() === loantypeoption.toLocaleLowerCase().trim()
      )[0] || [];
    if (Array.isArray(loanProduct)) {
      return loantypeoption;
    }
    return loanProduct?.productName ? loanProduct.productName : loantypeoption;
  }

  private getFromSession(key: string): string {
    return !this.isBrowser || !sessionStorage.getItem(key) ? undefined : sessionStorage.getItem(key);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onChangeLoantype(loanProduct: any): void {
    this.statefulService.wrap('ss:ola-new-loan-type').value = this.loanType;
    const uiTemplateId = loanProduct.uiTemplateId;
    if (this.selectedConsumerLoanGroup && this.selectedConsumerLoanGroup.loanProducts) {
      this.statefulService.wrap('ola:olaApplicationInformation/loanGroupIcon').value = this.selectedConsumerLoanGroup.groupIcon;
      const selectedLoanProduct = this.selectedConsumerLoanGroup.loanProducts.filter(
        (t) => !this.selectedConsumerLoanGroup?.isLoanGroupHasProduct || t.productName === this.loanType
      )[0];
      if (selectedLoanProduct) {
        this.loanProductDescriptionEnabled =
          selectedLoanProduct.isDescriptionEnabled || (!selectedLoanProduct.version && selectedLoanProduct.description);
        this.loanProductDescription = selectedLoanProduct.description;
        this.statefulService.wrap(this.checklistDescriptionPath).value = selectedLoanProduct?.checklistDescription;
        this.statefulService.wrap(this.isEnabledChecklistPath).value = selectedLoanProduct?.isEnabledChecklist;
        if (this.isPrefillByParams && this.loanType) {
          this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value = this.loanType;
        }
        this.statefulService.wrap('ss:hide-ola-requested-loan-amount').value = !selectedLoanProduct.ignoreLoanAmountSettings;
      }
    }
    // With the UITemplate having a dependency on the Loan Group & Loan Product,
    // re-ordering it to go after the Loan Product Name in the PATCH call series especially in case of the URL having params
    this.setUITemplate(uiTemplateId);
  }

  public get toggleAllProductsText(): string {
    const showLess = 'Show less options';
    const showMore = `Show ${this.consumerLoanGroups.length - 8} more option${this.consumerLoanGroups.length - 8 > 1 ? 's' : ''}`;
    return this.allProductsVisible ? showLess : showMore;
  }

  private setUITemplate(uiTemplateId): void {
    if (
      this.adminPortalFeaturesService?.config &&
      this.adminPortalFeaturesService?.config?.olaConfiguration &&
      this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates
    ) {
      const masterUITemplates = this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates.filter(
        (t) => t.uiTemplateId === uiTemplateId
      );
      const newUITemplate = masterUITemplates ? masterUITemplates[0].templateName : '';
      this.statefulService.wrap('ss:ola-new-ui-template').value = newUITemplate;

      if (newUITemplate) {
        this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').value = newUITemplate;
      }

      if (masterUITemplates[0].path) {
        const flow = this.flowService.createFlow(masterUITemplates[0].path);
        const eligiblity = flow.allPages.find((t) => t.id === 'ola-eligibility');
        this.statefulService.wrap('ola:skipEligibility').value = !eligiblity?.visible;
        this.statefulService.wrap('ola:olaApplicationInformation/isEligibilityCompleted').value =
          this.statefulService.wrap('ola:skipEligibility').value;
      }
    }
  }

  public get isLoanGroupHasProduct(): boolean {
    return this.selectedConsumerLoanGroup?.isLoanGroupHasProduct;
  }

  public get isLoanGroupHasNoProduct(): boolean {
    return !this.selectedConsumerLoanGroup?.isLoanGroupHasProduct;
  }
}
